<template>
  <v-dialog
    :model-value="modelValue"
    width="640"
    scrollable
    :aria-label="t('appBeta.dialog.title')"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="cc-app-beta-dialog">
      <v-card-title class="d-flex align-center">
        <v-icon
          class="mr-3"
          :icon="Icons.ROCKET"
        />
        <span>{{ t('appBeta.dialog.title') }}</span>
      </v-card-title>

      <v-card-text>
        <div
          v-if="loading"
          class="text-body-1"
        >
          {{ t('appBeta.dialog.loading') }}
        </div>

        <!-- Step 1: which store the tester needs. The two tracks have nothing
             in common beyond the word "Beta", so everything below depends on it. -->
        <template v-else-if="step === 'platform'">
          <p class="text-body-1 mb-2">
            {{ t('appBeta.dialog.platform.question') }}
          </p>
          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ t('appBeta.dialog.platform.info') }}
          </p>
          <div class="d-flex flex-column ga-3">
            <v-btn
              class="cc-app-beta-dialog__platform"
              variant="outlined"
              color="primary"
              size="large"
              :prepend-icon="Icons.APPLE"
              @click="choosePlatform('ios')"
            >
              {{ t('appBeta.dialog.platform.ios') }}
            </v-btn>
            <v-btn
              class="cc-app-beta-dialog__platform"
              variant="outlined"
              color="primary"
              size="large"
              :prepend-icon="Icons.ANDROID"
              @click="choosePlatform('android')"
            >
              {{ t('appBeta.dialog.platform.android') }}
            </v-btn>
          </div>
        </template>

        <!-- Step 2: the store account address. Both tracks are closed tests, so
             an invitation can only go to the exact Apple ID / Google account. -->
        <template v-else-if="step === 'email'">
          <p class="text-body-1 mb-2">
            {{ t(`appBeta.dialog.email.${platform}Question`) }}
          </p>
          <v-alert
            class="mb-4"
            type="info"
            role="status"
            variant="tonal"
            density="comfortable"
            :icon="Icons.INFORMATION_OUTLINE"
            :text="t(`appBeta.dialog.email.${platform}Hint`)"
          />
          <v-text-field
            v-model="email"
            variant="outlined"
            type="email"
            autocomplete="email"
            :label="t('appBeta.dialog.email.label')"
            :prepend-inner-icon="Icons.EMAIL"
            :error-messages="emailError ? [emailError] : []"
            :disabled="submitting"
            @keydown.enter="submit()"
          />
          <p
            v-if="submitError"
            class="text-body-2 text-error"
          >
            {{ t('appBeta.dialog.email.error') }}
          </p>
        </template>

        <!-- Step 3: what the tester has to do now, per platform. -->
        <template v-else>
          <h3 class="text-h6 mb-2">
            {{ t('appBeta.dialog.instructions.headline') }}
          </h3>
          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ t('appBeta.dialog.instructions.registeredWith', {email}) }}
          </p>
          <p class="text-body-1 mb-3">
            {{ t('appBeta.dialog.instructions.info') }}
          </p>
          <ol class="cc-app-beta-dialog__steps mb-6">
            <li
              v-for="(instruction, index) in steps"
              :key="`step-${index}`"
              class="text-body-2 mb-2"
            >
              {{ instruction }}
            </li>
          </ol>

          <template v-if="platform === 'android'">
            <v-btn
              class="mb-1"
              variant="outlined"
              color="primary"
              :href="ANDROID_TESTING_URL"
              target="_blank"
              rel="noopener"
              :append-icon="Icons.OPEN_IN_NEW"
            >
              {{ t('appBeta.dialog.instructions.linkLabel') }}
            </v-btn>
            <p class="text-body-2 text-medium-emphasis mb-6">
              {{ t('appBeta.dialog.instructions.linkHint') }}
            </p>
          </template>

          <h3 class="text-h6 mb-3">
            <v-icon
              class="mr-2"
              :icon="Icons.BUG_OUTLINE"
            />
            {{ t('appBeta.dialog.instructions.reportHeadline') }}
          </h3>
          <ul class="cc-app-beta-dialog__reports">
            <li
              v-for="(report, index) in reports"
              :key="`report-${index}`"
              class="text-body-2 mb-2"
            >
              {{ report }}
            </li>
          </ul>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="step === 'email'"
          variant="text"
          :prepend-icon="Icons.ARROW_LEFT"
          :disabled="submitting"
          @click="step = 'platform'"
        >
          {{ t('appBeta.dialog.back') }}
        </v-btn>
        <v-btn
          v-if="step === 'instructions'"
          variant="text"
          @click="step = 'platform'"
        >
          {{ t('appBeta.dialog.instructions.change') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="step === 'email'"
          color="primary"
          variant="elevated"
          :loading="submitting"
          @click="submit()"
        >
          {{ t('appBeta.dialog.email.submit') }}
        </v-btn>
        <v-btn
          v-else
          variant="text"
          @click="emit('update:modelValue', false)"
        >
          {{ step === 'instructions' ? t('appBeta.dialog.done') : t('appBeta.dialog.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {AppBetaApi} from '@/common/api/AppBetaApi';
import {IAppBetaSignup} from '@/types/components/common/IAppBetaSignup';
import {Icons} from '@simpleforyou/client-core';
import {useApiClient} from '@/common/api/useApiClient';
//================= [VUE COMPONENTS] ==========================
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';

const props = defineProps<{modelValue: boolean}>();
const emit = defineEmits<{(event: 'update:modelValue', value: boolean): void}>();

const {t} = useI18n();
const apiClient = useApiClient();

// Play Console opt-in page of the closed track. Only resolves for accounts we
// already added there, which is what instructions.linkHint says.
const ANDROID_TESTING_URL = 'https://play.google.com/apps/testing/com.communitycoaching.app';
const STEP_COUNT = 5;
const REPORT_COUNT = 3;

type AppBetaPlatform = 'ios' | 'android';
type AppBetaStep = 'platform' | 'email' | 'instructions';

const step = ref<AppBetaStep>('platform');
const platform = ref<AppBetaPlatform>('ios');
const email = ref('');
const emailError = ref('');
const submitError = ref(false);
const submitting = ref(false);
const loading = ref(false);
// The signup is only read once per session — reopening the dialog must not
// throw away a step the tester is in the middle of.
const loaded = ref(false);

const steps = computed(() => range(STEP_COUNT).map((index) => t(`appBeta.dialog.${platform.value}.step${index}`)));
const reports = computed(() => range(REPORT_COUNT).map((index) => t(`appBeta.dialog.${platform.value}.report${index}`)));

function range(count: number): number[] {
  return Array.from({length: count}, (_value, index) => index + 1);
}

const choosePlatform = (chosen: AppBetaPlatform) => {
  platform.value = chosen;
  emailError.value = '';
  submitError.value = false;
  step.value = 'email';
};

const submit = async () => {
  const address = email.value.trim();
  if (address === '') {
    emailError.value = t('appBeta.dialog.email.required');
    return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
    emailError.value = t('appBeta.dialog.email.invalid');
    return;
  }

  emailError.value = '';
  submitError.value = false;
  submitting.value = true;
  try {
    await apiClient.post(AppBetaApi.api(), {platform: platform.value, email: address});
    email.value = address;
    step.value = 'instructions';
  } catch {
    submitError.value = true;
  } finally {
    submitting.value = false;
  }
};

// Someone who already signed up mostly reopens the banner to read the setup
// steps again — so jump straight to them instead of asking twice.
const loadSignup = async () => {
  loading.value = true;
  try {
    const response = await apiClient.get<IAppBetaSignup>(AppBetaApi.api());
    if (response.data.registered && response.data.platform) {
      platform.value = response.data.platform;
      email.value = response.data.email ?? '';
      step.value = 'instructions';
    }
  } catch {
    // Falling back to the platform question is harmless: signing up again
    // overwrites the stored row instead of creating a second one.
  } finally {
    loaded.value = true;
    loading.value = false;
  }
};

// immediate: the banner mounts the dialog closed, but a mounted-open dialog
// (or a reopen) must read the signup too.
watch(() => props.modelValue, (open) => {
  if (open && !loaded.value) {
    void loadSignup();
  }
}, {immediate: true});
</script>

<style scoped lang="scss">
.cc-app-beta-dialog__platform {
  justify-content: flex-start;
}

.cc-app-beta-dialog__steps,
.cc-app-beta-dialog__reports {
  padding-left: 20px;
}
</style>
